import {
  Account,
  AccountTypeEnum,
  previewRecipientCount,
  SendTo,
  TenantMemberOrTeamDisplayData,
} from '@kudos/http-client'
import { getUserDisplayName } from '@shared/userDisplay/UserNameDisplay'
import { getSendToFromTenantMemberAndTeams } from '@shared/utils/getSendToFromTenantMemberAndTeams'
import { useMutation } from '@tanstack/react-query'
import { T, useT } from '@transifex/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import useRecognitionModal from '../useRecognitionModal'
import PointsEstimateInfoDisplay from './PointsEstimateInfoDisplay'

type DelegatorPointsEstimateDisplayProps = {
  amount: number
  sendTo: TenantMemberOrTeamDisplayData[]
  delegatorId: string
  onRecipientCountChange?: (count: number) => void
  onAllocationPointsChange?: (points: number) => void
  delegatorAccounts: Account[]
  isDelegatorPointsFetched: boolean
}

const DelegatorPointsEstimateDisplay: React.FC<DelegatorPointsEstimateDisplayProps> = ({
  amount,
  sendTo,
  delegatorId,
  onRecipientCountChange,
  onAllocationPointsChange,
  delegatorAccounts,
  isDelegatorPointsFetched,
}) => {
  const t = useT()
  const { dialogData } = useRecognitionModal()
  const delegator =
    dialogData?.delegatorFirstName || dialogData?.delegatorLastName
      ? { firstName: dialogData.delegatorFirstName, lastName: dialogData.delegatorLastName }
      : undefined
  const delegatorName = getUserDisplayName(delegator, t)
  const [recipientCount, setRecipientCount] = useState<number>(0)
  const onRecipientCountChangeRef = useRef(onRecipientCountChange)
  onRecipientCountChangeRef.current = onRecipientCountChange

  const { mutateAsync: previewDelegationRecipients, isPending: isPreviewPending } = useMutation({
    mutationFn: ({ delegatorId, sendTo }: { delegatorId: string; sendTo: SendTo[] }) =>
      previewRecipientCount({ sendTo, delegatorId }),
  })

  const allocationPoints = useMemo(() => {
    if (!delegatorAccounts) return 0
    const delegatorAllocationAccount = delegatorAccounts.find(
      account => account.type === AccountTypeEnum.ALLOCATION,
    )
    return delegatorAllocationAccount?.balance ?? 0
  }, [delegatorAccounts])

  useEffect(() => {
    if (isDelegatorPointsFetched) {
      onAllocationPointsChange?.(allocationPoints)
    }
  }, [allocationPoints, isDelegatorPointsFetched, onAllocationPointsChange])

  useEffect(() => {
    if (sendTo.length > 0 && delegatorId && !isPreviewPending) {
      const sendToData = getSendToFromTenantMemberAndTeams(sendTo)
      previewDelegationRecipients({ delegatorId, sendTo: sendToData }).then(
        ({ recipientCount }) => {
          setRecipientCount(recipientCount)
          onRecipientCountChangeRef.current?.(recipientCount)
        },
      )
    } else {
      setRecipientCount(0)
      onRecipientCountChangeRef.current?.(0)
    }
  }, [sendTo.length, delegatorId])

  const totalSpend = useMemo(() => {
    if (recipientCount > 0 && amount > 0) {
      return recipientCount * amount
    }
    return 0
  }, [recipientCount, amount])

  const showEstimate = useMemo(() => {
    return amount > 0 && recipientCount > 0 && totalSpend <= allocationPoints
  }, [amount, recipientCount, totalSpend, allocationPoints])

  if (!isDelegatorPointsFetched) {
    return null
  }

  const remainingMessage = (
    <T
      _str="{name} has {remaining} left to give"
      name={<b>{delegatorName}</b>}
      remaining={
        <b>
          <span>
            <T
              _str="{remainingNum, plural, one {{remaining} point} other {{remaining} points}}"
              remainingNum={allocationPoints}
              remaining={allocationPoints.toLocaleString()}
            />
          </span>
        </b>
      }
    />
  )

  const afterSendingMessage = (
    <T
      _str="After sending this message, {name} will have {points} left to give"
      name={<b>{delegatorName}</b>}
      points={
        <b>
          <span>
            <T
              _str="{remainingNum, plural, one {{remaining} point} other {{remaining} points}}"
              remainingNum={allocationPoints - totalSpend}
              remaining={(allocationPoints - totalSpend).toLocaleString()}
            />
          </span>
        </b>
      }
    />
  )

  return (
    <PointsEstimateInfoDisplay
      amount={amount}
      recipientCount={recipientCount}
      allocationPoints={allocationPoints}
      totalSpend={totalSpend}
      showEstimate={showEstimate}
      remainingMessage={remainingMessage}
      afterSendingMessage={afterSendingMessage}
    />
  )
}

export default DelegatorPointsEstimateDisplay
